import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { 
  User, Building2, MapPin, CheckCircle2, ArrowRight, ArrowLeft, 
  Briefcase, FileText, Sparkles, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// US States list
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' }, { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' }, { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' }, { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' }, { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' }, { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' }, { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' }, { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

const FILING_STATUSES = [
  { 
    value: 'single', 
    label: 'Single', 
    description: 'Unmarried or legally separated',
    icon: User
  },
  { 
    value: 'married_filing_jointly', 
    label: 'Married Filing Jointly', 
    description: 'Married and filing a combined return',
    icon: User
  },
  { 
    value: 'married_filing_separately', 
    label: 'Married Filing Separately', 
    description: 'Married but filing individual returns',
    icon: User
  },
  { 
    value: 'head_of_household', 
    label: 'Head of Household', 
    description: 'Unmarried with qualifying dependent',
    icon: User
  }
];

const BUSINESS_TYPES = [
  { 
    value: 'sole_proprietor', 
    label: 'Sole Proprietor', 
    description: 'Individual business owner (Schedule C)',
    icon: User
  },
  { 
    value: 'llc', 
    label: 'LLC (Single-Member)', 
    description: 'Limited Liability Company taxed as sole prop',
    icon: Building2
  },
  { 
    value: 's_corp', 
    label: 'S-Corporation', 
    description: 'Pass-through corporation with payroll requirements',
    icon: Building2
  },
  { 
    value: 'c_corp', 
    label: 'C-Corporation', 
    description: 'Traditional corporation with double taxation',
    icon: Building2
  },
  { 
    value: 'partnership', 
    label: 'Partnership', 
    description: 'Multi-member business entity',
    icon: Building2
  }
];

const ProfileSetupModal = ({ isOpen, onComplete, onSkip }) => {
  const { getAuthHeaders } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [filingStatus, setFilingStatus] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [state, setState] = useState('');
  const [hasW2Income, setHasW2Income] = useState(false);
  const [neverShowAgain, setNeverShowAgain] = useState(false);

  const totalSteps = 4; // Welcome, Filing Status, Business Type, State
  const progress = ((step + 1) / totalSteps) * 100;

  const handleNext = () => {
    if (step === 1 && !filingStatus) {
      toast.error('Please select a filing status');
      return;
    }
    if (step === 2 && !businessType) {
      toast.error('Please select a business type');
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleComplete = async () => {
    if (!state) {
      toast.error('Please select your state');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/user/onboarding/profile-setup`, {
        filing_status: filingStatus,
        business_type: businessType,
        state: state,
        has_w2_income: hasW2Income
      }, {
        headers: getAuthHeaders()
      });

      toast.success('Profile setup complete! Let\'s get started.');
      onComplete?.();
    } catch (error) {
      toast.error('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const [snoozeHours, setSnoozeHours] = useState(24);   // D3: explicit snooze duration

  const handleSkip = async () => {
    try {
      await axios.post(`${API}/user/onboarding/skip`, {
        skip_type: neverShowAgain ? 'never_show' : 'snooze',
        snooze_hours: neverShowAgain ? null : snoozeHours,   // D3: snooze duration
      }, { withCredentials: true });

      toast.info(neverShowAgain
        ? 'Setup skipped. You can configure these in Settings anytime.'
        : `We'll remind you in ${snoozeHours < 48 ? snoozeHours + 'h' : Math.round(snoozeHours / 24) + ' days'}.`
      );
      onSkip?.();
    } catch (error) {
      onSkip?.();
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <div className="text-center space-y-6 py-4">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Welcome to SolelyTax AI</h2>
              <p className="text-gray-600 mt-2">
                Let's personalize your tax experience in just a few steps
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-4 text-left space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Personalized Tax Workflow</p>
                  <p className="text-sm text-gray-500">Get step-by-step guidance tailored to your situation</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Business-Specific Features</p>
                  <p className="text-sm text-gray-500">Tools optimized for your business structure</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Track Your Progress</p>
                  <p className="text-sm text-gray-500">Never miss a step with our guided checklist</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6 py-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">How will you file your taxes?</h2>
              <p className="text-gray-600 mt-1">Select your filing status for this tax year</p>
            </div>
            
            <div className="space-y-3">
              {FILING_STATUSES.map((status) => (
                <button
                  key={status.value}
                  data-testid={`onboarding-filing-${status.value}`}
                  onClick={() => setFilingStatus(status.value)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    filingStatus === status.value
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-medium ${filingStatus === status.value ? 'text-emerald-700' : 'text-gray-900'}`}>
                        {status.label}
                      </p>
                      <p className="text-sm text-gray-500 mt-0.5">{status.description}</p>
                    </div>
                    {filingStatus === status.value && (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* W-2 Income checkbox - show for non-MFJ filers */}
            {filingStatus && filingStatus !== 'married_filing_jointly' && (
              <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <Checkbox 
                  id="w2-income"
                  checked={hasW2Income}
                  onCheckedChange={setHasW2Income}
                  className="mt-0.5"
                />
                <div>
                  <Label htmlFor="w2-income" className="font-medium text-blue-900 cursor-pointer">
                    I also have W-2 employment income
                  </Label>
                  <p className="text-sm text-blue-700 mt-0.5">
                    Check this if you're a gig worker who also has a regular job
                  </p>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6 py-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">What's your business structure?</h2>
              <p className="text-gray-600 mt-1">This helps us show you relevant tax features</p>
            </div>
            
            <div className="space-y-3">
              {BUSINESS_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    data-testid={`onboarding-business-${type.value}`}
                    onClick={() => setBusinessType(type.value)}
                    className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                      businessType === type.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          businessType === type.value ? 'bg-emerald-100' : 'bg-gray-100'
                        }`}>
                          <Icon className={`w-5 h-5 ${businessType === type.value ? 'text-emerald-600' : 'text-gray-600'}`} />
                        </div>
                        <div>
                          <p className={`font-medium ${businessType === type.value ? 'text-emerald-700' : 'text-gray-900'}`}>
                            {type.label}
                          </p>
                          <p className="text-sm text-gray-500">{type.description}</p>
                        </div>
                      </div>
                      {businessType === type.value && (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {businessType === 's_corp' && (
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>S-Corp owners:</strong> We'll guide you through reasonable salary requirements 
                  and help you optimize your salary vs. distributions for maximum tax savings.
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6 py-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">Where do you live?</h2>
              <p className="text-gray-600 mt-1">We'll calculate your state tax obligations</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-gray-700">State of Residence</Label>
              <Select value={state} onValueChange={setState}>
                <SelectTrigger className="w-full h-12 text-base" data-testid="onboarding-state-select">
                  <SelectValue placeholder="Select your state" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {US_STATES.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`onboarding-state-${s.value}`}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {state && (
              <div className="p-4 bg-gray-50 rounded-xl border">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium text-gray-900">
                    {US_STATES.find(s => s.value === state)?.label}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  We'll include {US_STATES.find(s => s.value === state)?.label} state tax calculations 
                  in your estimates and reports.
                </p>
              </div>
            )}

            {/* Summary */}
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
              <p className="font-medium text-emerald-800 mb-2">Your Profile Summary</p>
              <div className="space-y-1 text-sm text-emerald-700">
                <p>• Filing Status: <strong>{FILING_STATUSES.find(f => f.value === filingStatus)?.label}</strong></p>
                <p>• Business Type: <strong>{BUSINESS_TYPES.find(b => b.value === businessType)?.label}</strong></p>
                {hasW2Income && <p>• Also has W-2 employment income</p>}
                {state && <p>• State: <strong>{US_STATES.find(s => s.value === state)?.label}</strong></p>}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !saving) handleSkip(); }}>
      <DialogContent 
        className="sm:max-w-[500px] p-0 gap-0 overflow-hidden"
        data-testid="profile-setup-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Progress bar */}
        <div className="px-6 pt-6">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-gray-500 mt-2 text-center">
            Step {step + 1} of {totalSteps}
          </p>
        </div>

        {/* Content */}
        <div className="px-6 pb-2 max-h-[60vh] overflow-y-auto">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t">
          {step === 0 && (
            <div className="flex items-center justify-end gap-4 mb-3" data-testid="onboarding-skip-options">
                {step === 0 && (
                  <div className="flex items-center">
                    <Checkbox 
                      id="never-show"
                      data-testid="onboarding-never-show-checkbox"
                      checked={neverShowAgain}
                      onCheckedChange={setNeverShowAgain}
                      className="mr-2"
                    />
                    <Label htmlFor="never-show" className="text-xs text-gray-500 cursor-pointer">
                      Don't show again
                    </Label>
                  </div>
                )}

                {/* D3: Snooze duration selector */}
                {!neverShowAgain && step === 0 && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">in:</span>
                    {[{l:'24h',h:24},{l:'3d',h:72},{l:'1w',h:168}].map(opt => (
                      <button key={opt.h} type="button" onClick={() => setSnoozeHours(opt.h)} data-testid={`onboarding-snooze-${opt.l}`}
                        className={`text-xs px-1.5 py-0.5 rounded border ${
                          snoozeHours===opt.h ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 text-gray-500 hover:border-gray-400'
                        }`}>
                        {opt.l}
                      </button>
                    ))}
                  </div>
                )}
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button variant="ghost" onClick={handleBack} size="sm" data-testid="onboarding-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleSkip} size="sm" className="text-gray-500" data-testid="onboarding-skip-btn">
                {neverShowAgain ? "Skip Forever" : `Remind Me (${snoozeHours < 48 ? snoozeHours+'h' : Math.round(snoozeHours/24)+'d'})`}
              </Button>

              {step < 3 ? (
                <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700" data-testid="onboarding-next-btn">
                  {step === 0 ? "Get Started" : "Continue"}
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button 
                  onClick={handleComplete}
                  data-testid="onboarding-complete-btn"
                  disabled={saving || !state}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {saving ? "Saving..." : "Complete Setup"}
                  <CheckCircle2 className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileSetupModal;
